import React, { useState, useMemo } from 'react';
import {
  X,
  MapPin,
  Clock,
  Send,
  Info,
  AlertCircle,
  Heart,
  Briefcase,
  Check,
} from 'lucide-react';
import type {
  Place,
  MainCategory,
  GoodWorkCategory,
  Realm,
  MoodLabel,
  ActivityLabel,
  CommunityFeature,
  PlaceCreateInput,
  PlaceAmenities,
  PlacePricing,
  PlaceWorkspaces,
  PlaceGoodworkEvents,
  WifiQuality,
  PowerOutletAvailability,
} from '../../types';
import { MAIN_CATEGORIES, GOODWORK_CATEGORIES } from '../../types';

// GoodWork Labels
const WIFI_OPTIONS: { value: WifiQuality; label: string }[] = [
  { value: 'none', label: 'Kein WLAN' },
  { value: 'basic', label: 'WLAN verfügbar' },
  { value: 'fast', label: 'Schnelles WLAN' },
  { value: 'excellent', label: 'Sehr schnelles WLAN' },
];

const POWER_OPTIONS: { value: PowerOutletAvailability; label: string }[] = [
  { value: 'few', label: 'Wenige' },
  { value: 'some', label: 'Vorhanden' },
  { value: 'many', label: 'Viele' },
];
import { MOODS, ACTIVITIES, COMMUNITY_FEATURES } from '../../constants';
import { createSuggestion, hasUserPendingSuggestion } from '../../services/suggestionsService';

interface SuggestChangesModalProps {
  isOpen: boolean;
  place: Place;
  userId: string;
  userName: string;
  userEmail?: string;
  onClose: () => void;
  onShowNotification?: (message: string, type: 'success' | 'error' | 'info') => void;
}

export function SuggestChangesModal({
  isOpen,
  place,
  userId,
  userName,
  userEmail,
  onClose,
  onShowNotification,
}: SuggestChangesModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasPendingSuggestion, setHasPendingSuggestion] = useState(false);

  // Form state - initialized with current place data
  const [name, setName] = useState(place.name);
  const [address, setAddress] = useState(place.address || '');
  const [postalCode, setPostalCode] = useState(place.postalCode || '');
  const [city, setCity] = useState(place.city || '');
  // Multi-Realm support
  const [realms, setRealms] = useState<Realm[]>(place.realms || ['goodlife']);
  const [category, setCategory] = useState<MainCategory>(place.category || 'public_living_room');
  const [goodworkCategory, setGoodworkCategory] = useState<GoodWorkCategory | ''>(place.goodworkCategory || '');
  const [tagline, setTagline] = useState(place.tagline);
  const [description, setDescription] = useState(place.description || '');
  const [moods, setMoods] = useState<MoodLabel[]>(place.moods);
  const [activities, setActivities] = useState<ActivityLabel[]>(place.activities);
  const [community, setCommunity] = useState<CommunityFeature[]>(place.community);
  const [openingHoursText, setOpeningHoursText] = useState(place.openingHours?.displayText || '');
  const [website, setWebsite] = useState(place.website || '');
  const [phone, setPhone] = useState(place.phone || '');
  const [email, setEmail] = useState(place.email || '');

  // GoodWork specific fields
  const [goodworkFactor, setGoodworkFactor] = useState(place.goodworkFactor || '');
  const [amenities, setAmenities] = useState<PlaceAmenities>(place.amenities || {});
  const [pricing, setPricing] = useState<PlacePricing>(place.pricing || {});
  const [workspaces, setWorkspaces] = useState<PlaceWorkspaces>(place.workspaces || {});
  const [goodworkEvents, setGoodworkEvents] = useState<PlaceGoodworkEvents>(place.goodworkEvents || {});

  // Helper to update nested amenities
  const updateAmenities = (key: keyof PlaceAmenities, value: PlaceAmenities[keyof PlaceAmenities]) => {
    setAmenities((prev) => ({ ...prev, [key]: value }));
  };

  // Helper to update nested pricing
  const updatePricing = (key: keyof PlacePricing, value: PlacePricing[keyof PlacePricing]) => {
    setPricing((prev) => ({ ...prev, [key]: value }));
  };

  // Helper to update nested workspaces
  const updateWorkspaces = (key: keyof PlaceWorkspaces, value: PlaceWorkspaces[keyof PlaceWorkspaces]) => {
    setWorkspaces((prev) => ({ ...prev, [key]: value }));
  };

  // Helper to update nested goodworkEvents
  const updateGoodworkEvents = (key: keyof PlaceGoodworkEvents, value: PlaceGoodworkEvents[keyof PlaceGoodworkEvents]) => {
    setGoodworkEvents((prev) => ({ ...prev, [key]: value }));
  };

  // Realm toggle helpers
  const hasGoodLife = realms.includes('goodlife');
  const hasGoodWork = realms.includes('goodwork');

  const toggleRealm = (realm: Realm) => {
    setRealms((prev) => {
      if (prev.includes(realm)) {
        // Don't allow removing the last realm
        if (prev.length === 1) return prev;
        return prev.filter((r) => r !== realm);
      }
      return [...prev, realm];
    });
  };

  // Check if user already has pending suggestion on mount
  React.useEffect(() => {
    if (isOpen && userId && place.id) {
      hasUserPendingSuggestion(userId, place.id).then(setHasPendingSuggestion);
    }
  }, [isOpen, userId, place.id]);

  // Compute which fields have changed
  const changedFields = useMemo(() => {
    const changes: Set<string> = new Set();

    if (name !== place.name) changes.add('name');
    if (address !== (place.address || '')) changes.add('address');
    if (postalCode !== (place.postalCode || '')) changes.add('postalCode');
    if (city !== (place.city || '')) changes.add('city');
    if (category !== (place.category || 'public_living_room')) changes.add('category');
    if (goodworkCategory !== (place.goodworkCategory || '')) changes.add('goodworkCategory');
    if (tagline !== place.tagline) changes.add('tagline');
    if (description !== (place.description || '')) changes.add('description');
    if (openingHoursText !== (place.openingHours?.displayText || '')) changes.add('openingHours');
    if (website !== (place.website || '')) changes.add('website');
    if (phone !== (place.phone || '')) changes.add('phone');
    if (email !== (place.email || '')) changes.add('email');

    // Realms comparison
    const placeRealms = place.realms || ['goodlife'];
    const realmsChanged =
      realms.length !== placeRealms.length ||
      realms.some((r) => !placeRealms.includes(r));
    if (realmsChanged) changes.add('realms');

    // Array comparisons
    const moodsChanged =
      moods.length !== place.moods.length ||
      moods.some((m) => !place.moods.includes(m));
    if (moodsChanged) changes.add('moods');

    const activitiesChanged =
      activities.length !== place.activities.length ||
      activities.some((a) => !place.activities.includes(a));
    if (activitiesChanged) changes.add('activities');

    const communityChanged =
      community.length !== place.community.length ||
      community.some((c) => !place.community.includes(c));
    if (communityChanged) changes.add('community');

    // GoodWork fields
    if (goodworkFactor !== (place.goodworkFactor || '')) changes.add('goodworkFactor');

    // Deep compare objects
    if (JSON.stringify(amenities) !== JSON.stringify(place.amenities || {})) {
      changes.add('amenities');
    }
    if (JSON.stringify(pricing) !== JSON.stringify(place.pricing || {})) {
      changes.add('pricing');
    }
    if (JSON.stringify(workspaces) !== JSON.stringify(place.workspaces || {})) {
      changes.add('workspaces');
    }
    if (JSON.stringify(goodworkEvents) !== JSON.stringify(place.goodworkEvents || {})) {
      changes.add('goodworkEvents');
    }

    return changes;
  }, [
    name,
    address,
    postalCode,
    city,
    realms,
    category,
    goodworkCategory,
    tagline,
    description,
    moods,
    activities,
    community,
    openingHoursText,
    website,
    phone,
    email,
    goodworkFactor,
    amenities,
    pricing,
    workspaces,
    goodworkEvents,
    place,
  ]);

  const hasChanges = changedFields.size > 0;

  const toggleMood = (mood: MoodLabel) => {
    setMoods((prev) =>
      prev.includes(mood) ? prev.filter((m) => m !== mood) : [...prev, mood]
    );
  };

  const toggleActivity = (activity: ActivityLabel) => {
    setActivities((prev) =>
      prev.includes(activity) ? prev.filter((a) => a !== activity) : [...prev, activity]
    );
  };

  const toggleCommunity = (feature: CommunityFeature) => {
    setCommunity((prev) =>
      prev.includes(feature) ? prev.filter((c) => c !== feature) : [...prev, feature]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!hasChanges) {
      setError('Du hast keine Änderungen vorgenommen');
      return;
    }

    // Build changes object with only changed fields
    const changes: Partial<PlaceCreateInput> = {};

    if (changedFields.has('name')) changes.name = name.trim();
    if (changedFields.has('address')) changes.address = address.trim();
    if (changedFields.has('postalCode')) changes.postalCode = postalCode.trim();
    if (changedFields.has('city')) changes.city = city.trim();
    if (changedFields.has('realms')) changes.realms = realms;
    if (changedFields.has('category')) changes.category = category;
    if (changedFields.has('goodworkCategory') && goodworkCategory) {
      changes.goodworkCategory = goodworkCategory as GoodWorkCategory;
    }
    if (changedFields.has('tagline')) changes.tagline = tagline.trim();
    if (changedFields.has('description')) changes.description = description.trim();
    if (changedFields.has('moods')) changes.moods = moods;
    if (changedFields.has('activities')) changes.activities = activities;
    if (changedFields.has('community')) changes.community = community;
    if (changedFields.has('openingHours')) {
      changes.openingHours = { displayText: openingHoursText.trim() };
    }
    if (changedFields.has('website')) changes.website = website.trim() || undefined;
    if (changedFields.has('phone')) changes.phone = phone.trim() || undefined;
    if (changedFields.has('email')) changes.email = email.trim() || undefined;

    // GoodWork fields
    if (changedFields.has('goodworkFactor')) changes.goodworkFactor = goodworkFactor.trim() || undefined;
    if (changedFields.has('amenities')) changes.amenities = amenities;
    if (changedFields.has('pricing')) changes.pricing = pricing;
    if (changedFields.has('workspaces')) changes.workspaces = workspaces;
    if (changedFields.has('goodworkEvents')) changes.goodworkEvents = goodworkEvents;

    setIsLoading(true);
    try {
      await createSuggestion(
        place.id,
        place.name,
        changes,
        userId,
        userName,
        userEmail
      );

      onShowNotification?.(
        'Dein Änderungsvorschlag wurde eingereicht!',
        'success'
      );
      onClose();
    } catch (err) {
      console.error('Error creating suggestion:', err);
      setError('Fehler beim Senden. Bitte versuche es erneut.');
    } finally {
      setIsLoading(false);
    }
  };

  const getFieldClass = (field: string) => {
    // Map 'type' to 'category' for backwards compatibility
    const checkField = field === 'type' ? 'category' : field;
    return changedFields.has(checkField)
      ? 'border-amber-400 bg-amber-50'
      : '';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Änderung vorschlagen
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {hasPendingSuggestion ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-700">
                <p className="font-medium mb-1">Du hast bereits einen offenen Vorschlag</p>
                <p>
                  Warte bitte, bis dieser bearbeitet wurde, bevor du einen neuen Vorschlag einreichst.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Change indicator */}
              {hasChanges && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                  <span className="font-medium">{changedFields.size} Feld(er) geändert</span>
                  {' - '}Geänderte Felder sind gelb markiert
                </div>
              )}

              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                  Grunddaten
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name des Ortes
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={`input-field ${getFieldClass('name')}`}
                  />
                </div>

                {/* Realm Selection - Multi-Select Checkboxes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Art des Ortes
                    {changedFields.has('realms') && (
                      <span className="ml-2 text-xs text-amber-600 font-normal">(geändert)</span>
                    )}
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Ein Ort kann auch beides sein - z.B. ein Café das auch zum Arbeiten geeignet ist.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {/* GoodLife Checkbox */}
                    <button
                      type="button"
                      onClick={() => toggleRealm('goodlife')}
                      className={`p-3 rounded-xl border-2 transition text-left relative ${
                        hasGoodLife
                          ? 'border-emerald-600 bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {hasGoodLife && (
                        <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-emerald-600 flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          hasGoodLife
                            ? 'bg-emerald-600 text-white'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          <Heart className="w-4 h-4" />
                        </div>
                        <span className={`font-medium text-sm ${
                          hasGoodLife ? 'text-emerald-700' : 'text-gray-900'
                        }`}>
                          GoodLife
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Freizeit & Erleben</p>
                    </button>

                    {/* GoodWork Checkbox */}
                    <button
                      type="button"
                      onClick={() => toggleRealm('goodwork')}
                      className={`p-3 rounded-xl border-2 transition text-left relative ${
                        hasGoodWork
                          ? 'border-indigo-600 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {hasGoodWork && (
                        <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          hasGoodWork
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          <Briefcase className="w-4 h-4" />
                        </div>
                        <span className={`font-medium text-sm ${
                          hasGoodWork ? 'text-indigo-700' : 'text-gray-900'
                        }`}>
                          GoodWork
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Arbeiten & Fokus</p>
                    </button>
                  </div>
                </div>

                {/* GoodLife Category - nur anzeigen wenn GoodLife gewählt */}
                {hasGoodLife && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <span className="flex items-center gap-2">
                        <Heart className="w-4 h-4 text-emerald-600" />
                        GoodLife-Kategorie
                      </span>
                    </label>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value as MainCategory)}
                      className={`input-field ${getFieldClass('category')}`}
                    >
                      {MAIN_CATEGORIES.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* GoodWork Category - nur anzeigen wenn GoodWork gewählt */}
                {hasGoodWork && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <span className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-indigo-600" />
                        GoodWork-Kategorie
                      </span>
                    </label>
                    <select
                      value={goodworkCategory}
                      onChange={(e) => setGoodworkCategory(e.target.value as GoodWorkCategory)}
                      className={`input-field ${getFieldClass('goodworkCategory')}`}
                    >
                      <option value="">Bitte wählen...</option>
                      {GOODWORK_CATEGORIES.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      className={`input-field pl-10 ${getFieldClass('address')}`}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      PLZ
                    </label>
                    <input
                      type="text"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      className={`input-field ${getFieldClass('postalCode')}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stadt
                    </label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className={`input-field ${getFieldClass('city')}`}
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                  Beschreibung
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slogan
                  </label>
                  <input
                    type="text"
                    value={tagline}
                    onChange={(e) => setTagline(e.target.value)}
                    maxLength={60}
                    className={`input-field ${getFieldClass('tagline')}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Beschreibung
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    className={`input-field resize-none ${getFieldClass('description')}`}
                  />
                </div>
              </div>

              {/* Moods & Activities */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                  Charakter
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Atmosphaere
                    {changedFields.has('moods') && (
                      <span className="ml-2 text-xs text-amber-600 font-normal">(geaendert)</span>
                    )}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {MOODS.map((mood) => (
                      <button
                        key={mood.label}
                        type="button"
                        onClick={() => toggleMood(mood.label)}
                        className={`px-3 py-2 rounded-lg text-sm transition ${
                          moods.includes(mood.label)
                            ? 'bg-indigo-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {mood.icon} {mood.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Aktivitaeten
                    {changedFields.has('activities') && (
                      <span className="ml-2 text-xs text-amber-600 font-normal">(geaendert)</span>
                    )}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {ACTIVITIES.map((activity) => (
                      <button
                        key={activity.label}
                        type="button"
                        onClick={() => toggleActivity(activity.label)}
                        className={`px-3 py-2 rounded-lg text-sm transition ${
                          activities.includes(activity.label)
                            ? 'bg-purple-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {activity.icon} {activity.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gemeinschaft
                    {changedFields.has('community') && (
                      <span className="ml-2 text-xs text-amber-600 font-normal">(geaendert)</span>
                    )}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {COMMUNITY_FEATURES.map((feature) => (
                      <button
                        key={feature.label}
                        type="button"
                        onClick={() => toggleCommunity(feature.label)}
                        className={`px-3 py-2 rounded-lg text-sm transition ${
                          community.includes(feature.label)
                            ? 'bg-teal-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {feature.icon} {feature.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* GoodWork Details - Only shown for GoodWork places */}
              {hasGoodWork && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-indigo-600 uppercase tracking-wide flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    GoodWork Details
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Was macht diesen Ort zum GoodWork Place?
                      {changedFields.has('goodworkFactor') && (
                        <span className="ml-2 text-xs text-amber-600 font-normal">(geändert)</span>
                      )}
                    </label>
                    <textarea
                      value={goodworkFactor}
                      onChange={(e) => setGoodworkFactor(e.target.value)}
                      rows={3}
                      placeholder="z.B. Ruhige Atmosphäre, gutes WLAN, inspirierende Umgebung..."
                      className={`input-field resize-none ${changedFields.has('goodworkFactor') ? 'border-amber-400 bg-amber-50' : ''}`}
                    />
                  </div>

                  {/* Ausstattung */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ausstattung
                      {changedFields.has('amenities') && (
                        <span className="ml-2 text-xs text-amber-600 font-normal">(geändert)</span>
                      )}
                    </label>

                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">WLAN</label>
                        <select
                          value={amenities.wifi || 'none'}
                          onChange={(e) => updateAmenities('wifi', e.target.value as WifiQuality)}
                          className="input-field text-sm"
                        >
                          {WIFI_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Steckdosen</label>
                        <select
                          value={amenities.powerOutlets || ''}
                          onChange={(e) => updateAmenities('powerOutlets', e.target.value as PowerOutletAvailability || undefined)}
                          className="input-field text-sm"
                        >
                          <option value="">Keine Angabe</option>
                          {POWER_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {[
                        { key: 'monitors' as const, label: 'Monitore' },
                        { key: 'printer' as const, label: 'Drucker' },
                        { key: 'kitchen' as const, label: 'Küche' },
                        { key: 'meetingRooms' as const, label: 'Meeting-Räume' },
                        { key: 'phoneBooths' as const, label: 'Telefonkabinen' },
                        { key: 'locker' as const, label: 'Schließfächer' },
                      ].map((item) => (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => updateAmenities(item.key, !amenities[item.key])}
                          className={`px-3 py-2 rounded-lg text-sm transition ${
                            amenities[item.key]
                              ? 'bg-indigo-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Preise */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preise (optional)
                      {changedFields.has('pricing') && (
                        <span className="ml-2 text-xs text-amber-600 font-normal">(geändert)</span>
                      )}
                    </label>

                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Pro Stunde</label>
                        <div className="relative">
                          <input
                            type="number"
                            value={pricing.hourly || ''}
                            onChange={(e) => updatePricing('hourly', e.target.value ? Number(e.target.value) : undefined)}
                            placeholder="0"
                            className="input-field text-sm pr-8"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Pro Tag</label>
                        <div className="relative">
                          <input
                            type="number"
                            value={pricing.daily || ''}
                            onChange={(e) => updatePricing('daily', e.target.value ? Number(e.target.value) : undefined)}
                            placeholder="0"
                            className="input-field text-sm pr-8"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Pro Monat</label>
                        <div className="relative">
                          <input
                            type="number"
                            value={pricing.monthly || ''}
                            onChange={(e) => updatePricing('monthly', e.target.value ? Number(e.target.value) : undefined)}
                            placeholder="0"
                            className="input-field text-sm pr-8"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 mb-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={pricing.dropIn || false}
                          onChange={(e) => updatePricing('dropIn', e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700">Drop-in möglich</span>
                      </label>
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Buchungslink</label>
                      <input
                        type="url"
                        value={pricing.bookingUrl || ''}
                        onChange={(e) => updatePricing('bookingUrl', e.target.value || undefined)}
                        placeholder="https://..."
                        className="input-field text-sm"
                      />
                    </div>
                  </div>

                  {/* Arbeitsplätze */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Arbeitsplätze (optional)
                      {changedFields.has('workspaces') && (
                        <span className="ml-2 text-xs text-amber-600 font-normal">(geändert)</span>
                      )}
                    </label>

                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Anzahl Plätze</label>
                        <input
                          type="number"
                          value={workspaces.totalSeats || ''}
                          onChange={(e) => updateWorkspaces('totalSeats', e.target.value ? Number(e.target.value) : undefined)}
                          placeholder="0"
                          className="input-field text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Plätze draußen</label>
                        <input
                          type="number"
                          value={workspaces.outdoorSeats || ''}
                          onChange={(e) => updateWorkspaces('outdoorSeats', e.target.value ? Number(e.target.value) : undefined)}
                          placeholder="0"
                          className="input-field text-sm"
                        />
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {[
                        { key: 'quietZone' as const, label: 'Ruhebereich' },
                        { key: 'teamArea' as const, label: 'Teambereich' },
                        { key: 'standingDesks' as const, label: 'Stehplätze' },
                      ].map((item) => (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => updateWorkspaces(item.key, !workspaces[item.key])}
                          className={`px-3 py-2 rounded-lg text-sm transition ${
                            workspaces[item.key]
                              ? 'bg-indigo-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Community & Events */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Community & Events (optional)
                      {changedFields.has('goodworkEvents') && (
                        <span className="ml-2 text-xs text-amber-600 font-normal">(geändert)</span>
                      )}
                    </label>

                    <div className="flex flex-wrap gap-2 mb-3">
                      {[
                        { key: 'networking' as const, label: 'Networking Events' },
                        { key: 'workshops' as const, label: 'Workshops' },
                        { key: 'coworking' as const, label: 'Coworking Sessions' },
                      ].map((item) => (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => updateGoodworkEvents(item.key, !goodworkEvents[item.key])}
                          className={`px-3 py-2 rounded-lg text-sm transition ${
                            goodworkEvents[item.key]
                              ? 'bg-indigo-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Community-Beschreibung</label>
                      <input
                        type="text"
                        value={goodworkEvents.community || ''}
                        onChange={(e) => updateGoodworkEvents('community', e.target.value || undefined)}
                        placeholder="z.B. Jeden Freitag Gründer-Frühstück"
                        className="input-field text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Contact & Hours */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                  Kontakt & Oeffnungszeiten
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Oeffnungszeiten
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={openingHoursText}
                      onChange={(e) => setOpeningHoursText(e.target.value)}
                      className={`input-field pl-10 ${getFieldClass('openingHours')}`}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Website
                    </label>
                    <input
                      type="url"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      placeholder="https://..."
                      className={`input-field ${getFieldClass('website')}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className={`input-field ${getFieldClass('phone')}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      E-Mail
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`input-field ${getFieldClass('email')}`}
                    />
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="bg-blue-50 rounded-lg p-4 flex gap-3">
                <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium mb-1">Hinweis</p>
                  <p>
                    Dein Vorschlag wird von unseren Moderatoren geprueft.
                    Nur tatsaechlich geaenderte Felder werden uebermittelt.
                  </p>
                </div>
              </div>

              {/* Error */}
              {error && (
                <p className="text-red-500 text-sm p-3 bg-red-50 rounded-lg">
                  {error}
                </p>
              )}
            </form>
          )}
        </div>

        {/* Footer */}
        {!hasPendingSuggestion && (
          <div className="flex gap-3 p-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading || !hasChanges}
              className="flex-1 py-3 bg-indigo-500 text-white rounded-lg font-medium hover:bg-indigo-600 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" />
              {isLoading ? 'Wird gesendet...' : 'Vorschlag senden'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SuggestChangesModal;
